import { 
  collection, 
  addDoc, 
  doc, 
  getDoc, 
  getDocs,
  query, 
  where,
  orderBy, 
  updateDoc, 
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db, auth } from '../../firebaseConfig';
import { 
  WorkflowReference, 
  WorkflowStage, 
  CreateWorkflowReferenceParams,
  CreateWorkflowStageParams,
  VersionInfo,
  WorkflowDocument
} from '../../types/workflowTypes';
import { generateUniqueId } from '../../utils/idGenerator';
import { COLLECTIONS } from './constants';
import { AuditService } from './auditService';
import { getCurrentTimestamp, getDateValue } from './utils/dataUtils';
import { getEffectiveScopeUid } from '../../utils/tenantScope';

/**
 * Service class for managing workflow references and stages in Firestore
 */
export class WorkflowService {
  /**
   * Creates a new workflow reference
   * @param userId - User ID
   * @param params - Parameters for the new reference
   * @returns The created workflow reference with ID
   */
  static async createWorkflowReference(
    userId: string, 
    params: CreateWorkflowReferenceParams
  ): Promise<WorkflowReference> {
    const referenceId = generateUniqueId();
    const now = getCurrentTimestamp();

    // Create basic workflow reference
    const workflowReference: WorkflowReference = {
      referenceId,
      displayName: params.displayName,
      description: params.description || null,
      userId,
      createdAt: now,
      updatedAt: now,
      status: 'active',
      processType: params.processType || 'standard_sales',
      tags: params.tags || [],
      metadata: {
        // Include customer information directly in metadata
        ...(params.customerName ? { customerName: params.customerName } : {}),
        ...(params.customerId ? { customerId: params.customerId } : {}),
        ...(params.totalValue ? { totalValue: params.totalValue } : {})
      }
    };
    
    // Include metadata ONLY from params.metadata if available
    if (params.metadata) {
      // If customerName wasn't set directly but is in metadata
      if (params.customerName === undefined && params.metadata.customerName !== undefined) {
        workflowReference.metadata.customerName = params.metadata.customerName;
      }
      
      // If customerId wasn't set directly but is in metadata
      if (params.customerId === undefined && params.metadata.customerId !== undefined) {
        workflowReference.metadata.customerId = params.metadata.customerId;
      }
      
      // Add all other (unknown) metadata
      Object.keys(params.metadata).forEach(key => {
        const value = params.metadata![key];
        if (
          !workflowReference.metadata.hasOwnProperty(key) &&
          key !== 'customerName' &&
          key !== 'customerId' &&
          value !== undefined
        ) {
          workflowReference.metadata[key] = value;
        }
      });
    }

    const collectionRef = collection(db, COLLECTIONS.WORKFLOW_REFERENCES);
    await addDoc(collectionRef, {
      ...workflowReference,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Add audit log with customer information
    await AuditService.addAuditLog(userId, 'create', 'workflow_reference', referenceId, {
      displayName: params.displayName,
      customerName: params.customerName || workflowReference.metadata.customerName || null
    }, referenceId);

    return workflowReference;
  }

  /**
   * Gets all workflow references for a user
   * @param userId - User ID
   * @param options - Optional filter and sort options
   * @returns List of workflow references
   */
  static async getWorkflowReferences(
    userId: string, 
    options: {
      status?: 'active' | 'completed' | 'archived',
      customerId?: string,
      orderByField?: 'createdAt' | 'updatedAt' | 'displayName',
      orderDirection?: 'asc' | 'desc'
    } = {}
  ): Promise<WorkflowReference[]> {
    const collectionRef = collection(db, COLLECTIONS.WORKFLOW_REFERENCES);
    
    // Create base query
    let q = query(collectionRef, where('userId', '==', userId));
    
    // Filter by status if specified
    if (options.status) {
      q = query(q, where('status', '==', options.status));
    }
    
    // Filter by customer ID if specified
    if (options.customerId) {
      q = query(q, where('metadata.customerId', '==', options.customerId));
    }
    
    // Sorting
    const orderByField = options.orderByField || 'updatedAt';
    const orderDirection = options.orderDirection || 'desc';
    q = query(q, orderBy(orderByField, orderDirection));
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data() as WorkflowReference;
      return data;
    });
  }

  /**
   * Gets a single workflow reference by its ID
   * @param referenceId - ID of the workflow reference
   * @returns The workflow reference or null if not found
   */
  static async getWorkflowReference(referenceId: string): Promise<WorkflowReference | null> {
    // Since querying by referenceId causes issues with the rules,
    // we load ALL references for the current user and filter client-side.
    if (!auth.currentUser) {
      console.error('[WorkflowService] getWorkflowReference: No authenticated user found!');
      return null;
    }
    
    try {
      const allUserRefs = await this.getWorkflowReferences(getEffectiveScopeUid());
      const foundReference = allUserRefs.find(ref => ref.referenceId === referenceId);
      
      if (foundReference) {
        return foundReference;
      } else {
        console.warn(`[WorkflowService] getWorkflowReference: Reference with ID ${referenceId} not found among user's references.`);
        return null;
      }
    } catch (error) {
      console.error('[WorkflowService] getWorkflowReference: Error fetching all references for user:', error);
      throw error;
    }
  }

  /**
   * Updates a workflow reference
   * @param referenceId - ID of the reference to update
   * @param updates - Fields to update
   */
  static async updateWorkflowReference(
    referenceId: string, 
    updates: Partial<Omit<WorkflowReference, 'referenceId' | 'userId' | 'createdAt'>>
  ): Promise<void> {
    const collectionRef = collection(db, COLLECTIONS.WORKFLOW_REFERENCES);
    const q = query(collectionRef, where('referenceId', '==', referenceId));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error(`Workflow reference with ID ${referenceId} not found`);
    }
    
    const docRef = doc(db, COLLECTIONS.WORKFLOW_REFERENCES, querySnapshot.docs[0].id);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  }

  /**
   * Creates a new workflow stage
   * @param userId - User ID
   * @param params - Parameters for the new stage
   * @returns The created workflow stage with ID
   */
  static async createWorkflowStage(
    userId: string, 
    params: CreateWorkflowStageParams
  ): Promise<WorkflowStage> {
    // Check if the referenced workflow reference exists
    const workflowRef = await this.getWorkflowReference(params.referenceId);
    if (!workflowRef) {
      throw new Error(`Workflow reference with ID ${params.referenceId} not found`);
    }
    
    // If the workflow reference doesn't belong to the user, throw an error
    if (workflowRef.userId !== userId) {
      throw new Error('Access denied: You are not the owner of this workflow reference');
    }
    
    const now = getCurrentTimestamp();
    let documentDate: Timestamp;
    
    // Convert Date to Timestamp if necessary
    if (params.documentDate instanceof Date) {
      documentDate = Timestamp.fromDate(params.documentDate);
    } else {
      documentDate = params.documentDate;
    }
    
    // Create the base object with required fields
    const workflowStageData: any = {
      referenceId: params.referenceId,
      userId,
      stageType: params.stageType,
      documentNumber: params.documentNumber,
      documentDate,
      status: params.status,
      createdAt: now, // Use client-side timestamp for the object
      metadata: params.metadata || {}
    };
    
    // Add optional fields only if they have a value (not null/undefined)
    if (params.documentId) {
      workflowStageData.documentId = params.documentId;
    }
    if (params.notes) {
      workflowStageData.notes = params.notes;
    }
    if (params.amount !== undefined && params.amount !== null) {
      workflowStageData.amount = params.amount;
    }
    if (params.currency) {
      workflowStageData.currency = params.currency;
    }
    
    // NEW: Extended fields for relationships and versioning
    if (params.previousStageId) {
      // For backward compatibility
      workflowStageData.previousStageId = params.previousStageId;
      
      // If previousStageIds not explicitly set, set it based on previousStageId
      if (!params.previousStageIds) {
        workflowStageData.previousStageIds = [params.previousStageId];
      }
    }
    
    if (params.previousStageIds && params.previousStageIds.length > 0) {
      workflowStageData.previousStageIds = params.previousStageIds;
    }
    
    if (params.originalStageId) {
      workflowStageData.originalStageId = params.originalStageId;
    }
    
    // NEW: Add version information if available
    if (params.versionInfo) {
      const versionInfo: Partial<VersionInfo> = {
        version: params.versionInfo.version || 1,
        isLatestVersion: true,
        changedBy: userId,
        changeDate: now
      };
      
      if (params.versionInfo.changeReason) {
        versionInfo.changeReason = params.versionInfo.changeReason;
      }
      
      workflowStageData.versionInfo = versionInfo;
    }
    
    const collectionRef = collection(db, COLLECTIONS.WORKFLOW_STAGES);
    const docRef = await addDoc(collectionRef, {
      ...workflowStageData,
      // Override createdAt with server timestamp for the DB
      createdAt: serverTimestamp() 
    });
    
    // Add ID and return (use workflowStageData as base)
    const completeStage: WorkflowStage = {
      ...workflowStageData,
      id: docRef.id
    };
    
    // NEW: Add audit log for workflow stage creation
    await AuditService.addAuditLog(userId, 'create', 'workflow_stage', docRef.id, {
      stageType: params.stageType,
      documentNumber: params.documentNumber,
      referenceId: params.referenceId
    }, params.referenceId);
    
    return completeStage;
  }

  /**
   * Gets all workflow stages for a reference
   * @param referenceId - ID of the workflow reference
   * @param options - Optional filter and sort options
   * @returns List of workflow stages
   */
  static async getWorkflowStages(
    referenceId: string,
    options: {
      stageType?: string,
      status?: string,
      orderByField?: 'createdAt' | 'documentDate',
      orderDirection?: 'asc' | 'desc'
    } = {}
  ): Promise<WorkflowStage[]> {
    try {
      // 1. First check if the reference exists AND contains userId
      const reference = await this.getWorkflowReference(referenceId);
      if (!reference || !reference.userId) {
        console.warn(`Workflow reference with ID ${referenceId} not found or doesn't contain userId`);
        return [];
      }
      
      // 2. Get userId from the reference to use in the query
      const userId = reference.userId; 
      
      // 3. Create the Firestore query that filters by referenceId AND userId
      const collectionRef = collection(db, COLLECTIONS.WORKFLOW_STAGES);
      const q = query(
        collectionRef, 
        where('referenceId', '==', referenceId),
        where('userId', '==', userId)
      );
      
      try {
        // 4. Try to retrieve the documents (now with correct permissions)
        const querySnapshot = await getDocs(q);
        
        // 5. Process documents
        const stages = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          } as WorkflowStage));
        
        // 6. Client-side filtering by type (if needed)
        let filteredStages = [...stages];
        if (options.stageType) {
          filteredStages = filteredStages.filter(stage => 
            stage.stageType === options.stageType
          );
        }
        
        // 7. Client-side filtering by status
        if (options.status) {
          filteredStages = filteredStages.filter(stage => 
            stage.status === options.status
          );
        }
        
        // 8. Client-side sorting
        const orderByField = options.orderByField || 'createdAt';
        const orderDirection = options.orderDirection || 'desc';
        
        filteredStages.sort((a, b) => {
          const valA = getDateValue(a, orderByField === 'documentDate' ? 'documentDate' : 'createdAt');
          const valB = getDateValue(b, orderByField === 'documentDate' ? 'documentDate' : 'createdAt');
          
          return orderDirection === 'asc' ? valA - valB : valB - valA;
        });
        
        return filteredStages;
      } catch (error) {
        console.error('Error querying workflow stages:', error);
        
        // For permission errors: return empty array but don't treat as an error
        if (error && typeof error === 'object' && 'code' in error && error.code === 'permission-denied') {
          console.warn('Permission error accessing workflow stages - may not exist');
          return [];
        }
        
        throw error;
      }
    } catch (error) {
      console.error('Error in getWorkflowStages:', error);
      return [];
    }
  }

  /**
   * Gets a single workflow stage by its ID
   * @param stageId - ID of the workflow stage
   * @returns The workflow stage or null if not found
   */
  static async getWorkflowStage(stageId: string): Promise<WorkflowStage | null> {
    const docRef = doc(db, COLLECTIONS.WORKFLOW_STAGES, stageId);
    const docSnapshot = await getDoc(docRef);
    
    if (!docSnapshot.exists()) {
      return null;
    }
    
    return {
      id: docSnapshot.id,
      ...docSnapshot.data()
    } as WorkflowStage;
  }

  /**
   * Updates a workflow stage
   * @param stageId - ID of the stage to update
   * @param updates - Fields to update
   */
  static async updateWorkflowStage(
    stageId: string, 
    updates: Partial<Omit<WorkflowStage, 'id' | 'referenceId' | 'userId' | 'createdAt'>>
  ): Promise<void> {
    const docRef = doc(db, COLLECTIONS.WORKFLOW_STAGES, stageId);
    const docSnapshot = await getDoc(docRef);
    
    if (!docSnapshot.exists()) {
      throw new Error(`Workflow stage with ID ${stageId} not found`);
    }
    
    await updateDoc(docRef, updates);
    
    // Also update the workflow reference (updatedAt)
    const stageData = docSnapshot.data() as WorkflowStage;
    await this.updateWorkflowReference(stageData.referenceId, {
      updatedAt: getCurrentTimestamp()
    });
  }

  /**
   * Searches for workflow stages by document number
   * @param userId - User ID
   * @param documentNumber - The document number to search for
   * @returns Array of found workflow stages
   */
  static async findStagesByDocumentNumber(
    userId: string,
    documentNumber: string
  ): Promise<WorkflowStage[]> {
    const collectionRef = collection(db, COLLECTIONS.WORKFLOW_STAGES);
    const q = query(
      collectionRef,
      where('userId', '==', userId),
      where('documentNumber', '==', documentNumber)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      return {
        id: doc.id,
        ...doc.data()
      } as WorkflowStage;
    });
  }

  /**
   * Gets a list of all workflow references associated with a specific document number
   * @param userId - User ID
   * @param documentNumber - The document number (e.g., invoice number, order number)
   * @returns Array of workflow references
   */
  static async findReferencesByDocumentNumber(
    userId: string,
    documentNumber: string
  ): Promise<WorkflowReference[]> {
    // First find all stages with this document number
    const stages = await this.findStagesByDocumentNumber(userId, documentNumber);
    
    if (stages.length === 0) {
      return [];
    }
    
    // Collect all unique referenceIds
    const referenceIds = stages.map(stage => stage.referenceId);
    const uniqueReferenceIds = Array.from(new Set(referenceIds));
    
    // Get all associated workflow references
    const promises = uniqueReferenceIds.map(refId => this.getWorkflowReference(refId));
    const results = await Promise.all(promises);
    
    // Filter out null values (in case a reference doesn't exist)
    return results.filter(ref => ref !== null) as WorkflowReference[];
  }

  /**
   * NEW: Sets an existing workflow stage to "revised" and creates a new version
   * @param stageId - ID of the stage to revise
   * @param updatedData - Updated data for the new version
   * @param versionReason - Reason for the revision
   * @returns The newly created stage
   */
  static async createRevisionStage(
    stageId: string,
    updatedData: Partial<WorkflowStage>,
    versionReason: string
  ): Promise<WorkflowStage> {
    // Get the current stage
    const currentStage = await this.getWorkflowStage(stageId);
    if (!currentStage) {
      throw new Error(`Workflow stage with ID ${stageId} not found`);
    }
    
    // Check if the current user can modify the stage
    if (!auth.currentUser || currentStage.userId !== getEffectiveScopeUid()) {
      throw new Error('Access denied: You are not the owner of this workflow stage');
    }
    
    // Set the current stage to "revised"
    await this.updateWorkflowStage(stageId, { status: 'revised' });
    
    // Determine the original stage
    const originalStageId = currentStage.originalStageId || stageId;
    
    // Determine the version number
    let versionNumber = 1;
    if (currentStage.versionInfo && currentStage.versionInfo.version) {
      versionNumber = currentStage.versionInfo.version + 1;
    }
    
    // Set isLatestVersion of the previous version to false
    if (currentStage.versionInfo) {
      await this.updateWorkflowStage(stageId, {
        versionInfo: {
          ...currentStage.versionInfo,
          isLatestVersion: false
        }
      });
    }
    
    // Create a new version of the stage
    const newStageParams: CreateWorkflowStageParams = {
      referenceId: currentStage.referenceId,
      stageType: updatedData.stageType || currentStage.stageType,
      documentNumber: updatedData.documentNumber || currentStage.documentNumber,
      documentDate: updatedData.documentDate || currentStage.documentDate,
      documentId: updatedData.documentId || currentStage.documentId,
      status: updatedData.status || 'draft',
      notes: updatedData.notes || currentStage.notes,
      amount: updatedData.amount || currentStage.amount,
      currency: updatedData.currency || currentStage.currency,
      metadata: updatedData.metadata || currentStage.metadata,
      previousStageIds: [stageId],
      originalStageId: originalStageId,
      versionInfo: {
        version: versionNumber,
        changeReason: versionReason
      }
    };
    
    // Create the new stage
    return await this.createWorkflowStage(
      getEffectiveScopeUid(),
      newStageParams
    );
  }

  /**
   * NEW: Finds all documents in a workflow
   * @param workflowReferenceId - ID of the workflow reference
   * @returns List of all documents in the workflow (grouped by type)
   */
  static async getWorkflowDocuments(
    workflowReferenceId: string
  ): Promise<{
    offers: string[],
    invoices: string[],
    stages: WorkflowStage[]
  }> {
    // Get all stages for this workflow
    const stages = await this.getWorkflowStages(workflowReferenceId);
    
    // Initialize result structure
    const result = {
      offers: [] as string[],
      invoices: [] as string[],
      stages
    };
    
    // Collect document IDs based on stage type
    for (const stage of stages) {
      if (stage.documentId) {
        if (stage.stageType === 'quotation') {
          result.offers.push(stage.documentId);
        } else if (stage.stageType === 'invoice' || stage.stageType === 'invoice_draft') {
          result.invoices.push(stage.documentId);
        }
      }
    }
    
    return result;
  }
} 